import { useState, useRef, useMemo } from 'react';

const ProductDetails = ({ productInfo, onActivityClick, rightContent }) => {
  const [idx, setIdx] = useState(0);
  const [bigImg, setBigImg] = useState({
    url: '',
    bigX: 0,
    bigY: 0,
    maskX: 0,
    maskY: 0,
  });

  const imageRef = useRef(null);

  const maskWidth = 120; // 遮罩宽度
  const imgWidth = 260; // 图片宽度
  const bigImgWidth = 900; // 大图宽度

  const imgList = useMemo(() => {
    return (
      productInfo.images?.map((url) => ({
        type: 'img',
        url: url,
      })) || []
    );
  }, [productInfo.images]);

  const previewList = useMemo(() => {
    return imgList;
  }, [imgList]);

  const visibleThumbnails = useMemo(() => {
    const maxThumbnails = 3;
    let start = 0;
    let end = Math.min(maxThumbnails, previewList.length);

    if (previewList.length > maxThumbnails) {
      start = Math.max(0, idx - Math.floor(maxThumbnails / 2));
      start = Math.min(start, previewList.length - maxThumbnails);
      end = start + maxThumbnails;
    }

    return previewList.slice(start, end);
  }, [previewList, idx]);

  function handleMouseMoveEvent(e) {
    const image = imgList[idx]?.url;
    if (!image) {
      return;
    }
    const clientLeft = imageRef.current?.getBoundingClientRect()?.left || 0;
    const clientTop = imageRef.current?.getBoundingClientRect()?.top || 0;
    const moveX = e.clientX - clientLeft;
    const moveY = e.clientY - clientTop;
    const scale = bigImgWidth / imgWidth;
    let maskX = moveX - maskWidth / 2;
    let maskY = moveY - maskWidth / 2;
    if (maskX <= 0) {
      maskX = 0;
    } else if (maskX >= imgWidth - maskWidth) {
      maskX = imgWidth - maskWidth;
    }
    if (maskY <= 0) {
      maskY = 0;
    } else if (maskY >= imgWidth - maskWidth) {
      maskY = imgWidth - maskWidth;
    }
    setBigImg({
      ...bigImg,
      url: image,
      bigX: -maskX * scale,
      bigY: -maskY * scale,
      maskX,
      maskY,
    });
  }

  function handleMouseLeaveEvent() {
    setBigImg({
      ...bigImg,
      url: '',
    });
  }

  const preImg = () => {
    if (idx !== 0) {
      setIdx(idx - 1);
    }
  };

  const nextImg = () => {
    if (idx < previewList.length - 1) {
      setIdx(idx + 1);
    }
  };

  const thumbnailList = useMemo(() => {
    return (
      <div className="w-[260px] flex items-center h-[48px] relative mt-[10px]">
        <button
          aria-label="切换上一张图片"
          className="w-[14px] h-[12px] border-solid border-[#B8BEC9] border-r-[2px] border-b-[2px] transform rotate-[135deg] hover:border-[#199FE9] cursor-pointer ml-[6px] mr-[6px]"
          onClick={preImg}
        ></button>
        <ul className="flex-1 flex items-center justify-center">
          {visibleThumbnails?.map((item) => {
            const actualIndex = previewList.findIndex((i) => i === item);
            return (
              <li key={actualIndex} className="mr-[20px] last-of-type:mr-0">
                <img
                  className={`cursor-pointer rounded-[4px] w-[48px] h-[48px] object-cover ${actualIndex === idx ? 'border-solid border-[1px] border-[#199FE9]' : 'border-solid border-[1px] border-[#e5e7eb]'}`}
                  src={item.url}
                  alt={`${productInfo.name}商品缩略图`}
                  height={48}
                  width={48}
                  onClick={() => setIdx(actualIndex)}
                />
              </li>
            );
          })}
        </ul>
        <button
          aria-label="切换下一张图片"
          className="w-[14px] h-[12px] border-solid border-[#B8BEC9] border-r-[2px] border-b-[2px] transform rotate-[-45deg] hover:border-[#199FE9] cursor-pointer ml-[6px] mr-[6px]"
          onClick={nextImg}
        ></button>
      </div>
    );
  }, [productInfo.name, previewList, idx, visibleThumbnails]);

  return (
    <div className="pt-[30px] px-[30px] pb-[23px] bg-white">
      <div className="w-full">
        {/* Product Supply Info */}
        <div>
          <div>
            <h1 className="text-base font-bold text-black-444 inline-block">{productInfo.name}</h1>
            <span className="text-black-666 text-sm leading-2">({productInfo.status})</span>
          </div>
          <p className="mt-[9px] mb-[10px] text-base text-black-666">{productInfo.description}</p>
        </div>

        <div className="flex gap-5">
          {/* Product Images */}
          <div className="mb-5">
            <div className="relative">
              <div
                className="relative w-[260px] h-[260px]"
                onMouseMove={(e) => {
                  handleMouseMoveEvent(e);
                }}
                onMouseLeave={(e) => {
                  handleMouseLeaveEvent(e);
                }}
              >
                <img
                  className={`mb-[13px] rounded-[8px] h-[${imgWidth}px] w-[${imgWidth}px] object-cover`}
                  src={previewList[idx]?.url}
                  title="点击查看大图"
                  alt={`${productInfo.name}实物图`}
                  height={imgWidth}
                  width={imgWidth}
                  ref={imageRef}
                />
                {bigImg.url !== '' ? (
                  <div
                    className={`absolute bg-[lightblue] opacity-[0.4] z-[99]`}
                    style={{
                      width: maskWidth + 'px',
                      height: maskWidth + 'px',
                      top: bigImg.maskY + 'px',
                      left: bigImg.maskX + 'px',
                    }}
                  ></div>
                ) : (
                  <></>
                )}
              </div>
              {/** 查看大图 */}
              {bigImg.url !== '' ? (
                <section
                  className={`size-[400px] absolute left-[280px] top-[0px] z-[4] overflow-hidden border-solid border-[1px] border-[#e8e8e8]`}
                  style={{
                    backgroundImage: `url(${bigImg.url})`,
                    backgroundSize: `${bigImgWidth}px ${bigImgWidth}px`,
                    backgroundPositionX: bigImg.bigX + 'px',
                    backgroundPositionY: bigImg.bigY + 'px',
                    backgroundRepeat: 'no-repeat',
                  }}
                ></section>
              ) : (
                <></>
              )}
              {visibleThumbnails.length > 1 ? thumbnailList : <></>}
              <p className="text-[#54657D] text-[12px] mt-[4px]">温馨提醒：图片仅供参考，商品以实物为准</p>
            </div>
          </div>

          {/* Product Info */}
          <div className="flex-1">
            {/* Datasheet */}
            {productInfo.datasheet && (
              <div className="mb-5">
                <h3 className="text-sm font-bold mb-1">数据表</h3>
                <div className="text-gray-600 cursor-default text-xs mb-2">{productInfo.datasheet.title}</div>
                <div className="mb-3">
                  <button
                    id="item-similar-product"
                    className="text-[#FFFFFF] rounded-[4px] bg-[#0093E6] w-[100px] h-[28px] hover:opacity-[0.6]"
                    onClick={() => window.open(productInfo.datasheet.url, '_blank')}
                  >
                    立即查看
                  </button>
                </div>
                {/* Download Resources */}
                {productInfo.datasheet.downloadFiles && productInfo.datasheet.downloadFiles.length > 0 && (
                  <div className="flex flex-wrap gap-3">
                    {productInfo.datasheet.downloadFiles.map((file, index) => (
                      <a
                        key={index}
                        href={file.url}
                        title={file.name}
                        download
                        className="relative inline-block pl-6 pr-2 py-1 text-xs text-gray-700 hover:text-blue-600 transition-colors"
                        data-spm="xz"
                      >
                        <i
                          className="absolute left-1 top-1 w-[14px] h-[14px] bg-no-repeat"
                          style={{
                            backgroundImage:
                              'url(//static.szlcsc.com/ecp/assets/web/page/www/xinpinGD32E231/images/index_icon_2.png)',
                            backgroundPosition: '0px -18px',
                          }}
                        ></i>
                        <p>{file.name}</p>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Activities */}
            {productInfo.activities &&
              productInfo.activities.map((activity, index) => (
                <div key={index} className="mb-5 border rounded-md">
                  <h3 className="text-sm font-bold text-black">{activity.title}</h3>
                  <a
                    rel="nofollow"
                    onClick={activity.link === '#' ? onActivityClick : undefined}
                    href={activity.link !== '#' ? activity.link : undefined}
                    className="text-xs text-[#0093e6] font-medium cursor-pointer"
                    data-spm="ac"
                  >
                    {activity.description}
                  </a>
                </div>
              ))}
          </div>

          {/* Right Content */}
          {rightContent && <div className="w-[300px]">{rightContent}</div>}
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
